import { memo, useCallback, useState } from "react";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View } from "@src/components/View";
import { ActivityIndicator, Pressable, ScrollView } from "react-native";
import { Button, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import {
  UserListTable,
  UserListFilterRow,
  type UserListFilters,
  type UserListFilterSite,
  type UserListFilterCamera,
} from "@visioni/a3s-access/app";
import { useUsersAndPermissionsPage } from "@src/features/settings/hooks";

const UsersAndPermissionsScreen = memo(() => {
  const {
    permissionsLoading,
    permissionsError,
    isTenantAdmin,
    isReady,
    isError,
    filters,
    setFilters,
    filterSites,
    filterCameras,
    filteredUsers,
    handleInviteUser,
    handleEditUser,
  } = useUsersAndPermissionsPage();

  if (permissionsLoading || (!permissionsError && !isTenantAdmin)) {
    return (
      <View flex={1} alignItems="center" justifyContent="center">
        <ActivityIndicator />
      </View>
    );
  }

  if (permissionsError) {
    return (
      <View flex={1} alignItems="center" justifyContent="center" p="4xl">
        <Text textStyle="md/medium" color="text">
          Unable to verify permissions. Please try again later.
        </Text>
      </View>
    );
  }

  if (isError) {
    return (
      <View flex={1} alignItems="center" justifyContent="center" padding="3xl">
        <Text textStyle="sm/regular" color="textMuted" textAlign="center">
          Something went wrong. Please try again.
        </Text>
      </View>
    );
  }

  if (!isReady) {
    return (
      <View flex={1} alignItems="center" justifyContent="center">
        <ActivityIndicator />
      </View>
    );
  }

  return (
    <ScrollView style={styles.scrollView} contentContainerStyle={styles.contentContainerStyle}>
      <View flexDirection="row" alignItems="center" justifyContent="space-between" mb="xl">
        <Text textStyle="lg/semibold" color="text">
          Users
        </Text>
        <Button label="Invite User" variant="primary" size="sm" onPress={handleInviteUser} />
      </View>
      <CollapsibleFilters filters={filters} onFiltersChange={setFilters} sites={filterSites} cameras={filterCameras} />
      <UserListTable users={filteredUsers} onEditUser={handleEditUser} />
    </ScrollView>
  );
});

export default UsersAndPermissionsScreen;

const CollapsibleFilters = memo(
  ({
    filters,
    onFiltersChange,
    sites,
    cameras,
  }: {
    filters: UserListFilters;
    onFiltersChange: (filters: UserListFilters) => void;
    sites: readonly UserListFilterSite[];
    cameras: readonly UserListFilterCamera[];
  }) => {
    const { theme } = useUnistyles();

    const activeCount = [filters.search, filters.role, filters.siteId, filters.cameraId].filter(Boolean).length;
    // Open when arriving with filters already applied (e.g. from a camera) so they're visible.
    const [expanded, setExpanded] = useState(activeCount > 0);

    const handleToggle = useCallback(() => {
      setExpanded((prev) => !prev);
    }, []);

    return (
      <View mb="xl">
        <Pressable onPress={handleToggle} style={styles.filterToggle}>
          <Text textStyle="sm/medium" color="textMuted">
            Filters{activeCount > 0 ? ` (${activeCount})` : ""}
          </Text>
          <Icon name={expanded ? "chevronUp" : "chevronDown"} size={16} color={theme.colors.textMuted} />
        </Pressable>
        {expanded && (
          <View mt="md">
            <UserListFilterRow filters={filters} onFiltersChange={onFiltersChange} sites={sites} cameras={cameras} />
          </View>
        )}
      </View>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  scrollView: {
    flex: 1,
  },
  contentContainerStyle: {
    flexGrow: 1,
    padding: theme.spacing["3xl"],
  },
  filterToggle: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: theme.spacing.sm,
  },
}));
